import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { type FolderStatusCounts, foldersApi } from '@/api/folders.api';
import { AdminLayout } from '@/components/layout/AdminLayout';

/**
 * Admin-only folder overview. Shows every folder in the org with envelope
 * counts broken down by status — answers the customer's ask for admins
 * to "see the documents in folders better" without paging through the
 * envelopes list one folder at a time.
 *
 * Click on any folder → jumps to the envelopes list filtered to that
 * folder, so the workflow is: spot a problem here → drill in there.
 */
export function AdminFoldersPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'folders', 'overview'],
    queryFn: () => foldersApi.adminOverview(),
  });

  return (
    <AdminLayout
      title={t('admin.folders_title')}
      subtitle={t('admin.folders_subtitle')}
    >
      {isLoading ? (
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      ) : !data ? (
        <div className="card text-center py-12 text-muted">{t('common.no_data')}</div>
      ) : (
        <div className="card !p-0">
          <table className="table">
            <thead>
              <tr>
                <th>{t('admin.folders_name')}</th>
                <th className="text-right">{t('admin.folders_total')}</th>
                <th className="text-right">{t('envelope.status_draft')}</th>
                <th className="text-right">{t('envelope.status_sent')}</th>
                <th className="text-right">{t('envelope.status_in_progress')}</th>
                <th className="text-right">{t('envelope.status_completed')}</th>
                <th className="text-right">{t('envelope.status_declined')}</th>
                <th className="text-right">{t('envelope.status_voided')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.folders.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-8">
                    {t('admin.folders_empty')}
                  </td>
                </tr>
              )}
              {data.folders.map((f) => (
                <FolderRow key={f.id} id={f.id} name={f.name} color={f.color} counts={f.counts} />
              ))}
              <FolderRow
                id={null}
                name={t('admin.folders_unfiled')}
                color="#9CA3AF"
                counts={data.unfiled}
              />
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

function FolderRow({
  id,
  name,
  color,
  counts,
}: {
  id: string | null;
  name: string;
  color: string;
  counts: FolderStatusCounts;
}) {
  const { t } = useTranslation();
  const linkTo = id ? `/envelopes?folder=${id}` : '/envelopes?folder=unfiled';
  const stuck = counts.sent + counts.in_progress;

  return (
    <tr>
      <td>
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ background: color }}
          />
          <span className="font-medium text-gray-900">{name}</span>
        </div>
      </td>
      <td className="text-right font-semibold text-gray-900">{counts.total}</td>
      <td className="text-right text-muted">{counts.draft || '—'}</td>
      <td className="text-right">
        {counts.sent ? <span className="text-warning-fg font-medium">{counts.sent}</span> : <span className="text-muted">—</span>}
      </td>
      <td className="text-right">
        {counts.in_progress ? <span className="text-warning-fg font-medium">{counts.in_progress}</span> : <span className="text-muted">—</span>}
      </td>
      <td className="text-right">
        {counts.completed ? <span className="text-success font-medium">{counts.completed}</span> : <span className="text-muted">—</span>}
      </td>
      <td className="text-right">
        {counts.declined ? <span className="text-danger font-medium">{counts.declined}</span> : <span className="text-muted">—</span>}
      </td>
      <td className="text-right">
        {counts.voided ? <span className="text-danger font-medium">{counts.voided}</span> : <span className="text-muted">—</span>}
      </td>
      <td className="text-right whitespace-nowrap">
        {stuck > 0 && (
          <span
            className="text-[10px] uppercase tracking-wide bg-warning-bg text-warning-fg rounded px-1.5 py-0.5 mr-2"
            title={t('admin.folders_stuck_hint') as string}
          >
            ⚠ {stuck} {t('admin.folders_pending')}
          </span>
        )}
        <Link to={linkTo} className="btn btn-ghost btn-sm">
          {t('admin.folders_open')} →
        </Link>
      </td>
    </tr>
  );
}
